import api from './index'

import type { Middleware, UnknownAction } from '@reduxjs/toolkit'

type QueryThunkArg = { queryCacheKey: string; endpointName: string; originalArgs: unknown; forceRefetch?: boolean; direction?: string }
type QueryThunkAction = UnknownAction & { meta: { arg: QueryThunkArg; condition?: boolean } }
type RefetchableEndpoints = Record<string, { initiate: (arg: unknown, options: { subscribe: boolean; forceRefetch: boolean }) => unknown }>

const isQueryThunkAction = (action: unknown, reducerPath: string): action is QueryThunkAction =>
  typeof (action as UnknownAction)?.type === 'string' && (action as UnknownAction).type.startsWith(`${reducerPath}/executeQuery/`)

// RTK refuses a forced refetch while its entry is still loading, so an invalidation landing mid-load would be lost.
export const createRefusedRefetchRetry =
  ({ reducerPath, endpoints }: { reducerPath: string; endpoints: unknown }): Middleware =>
  store => {
    const owed = new Map<string, QueryThunkArg>()
    return next => action => {
      const result = next(action)
      if (!isQueryThunkAction(action, reducerPath) || action.type.endsWith('/pending')) return result
      const { arg, condition } = action.meta
      if (condition) {
        if (arg.forceRefetch && !arg.direction) owed.set(arg.queryCacheKey, arg)
        return result
      }
      const owedArg = owed.get(arg.queryCacheKey)
      if (owedArg) {
        owed.delete(arg.queryCacheKey)
        const endpoint = (endpoints as RefetchableEndpoints)[owedArg.endpointName]
        store.dispatch(endpoint.initiate(owedArg.originalArgs, { subscribe: false, forceRefetch: true }) as UnknownAction)
      }
      return result
    }
  }

export const refusedRefetchRetry = createRefusedRefetchRetry(api)
